import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight, MessageCircle, MoveDown } from "lucide-react";
import Reveal from "./Reveal";
import { scrollToHash } from "./scroll";
import goldenClean from "@/assets/golden_clean.png";
import pinkClean from "@/assets/pink_clean.png";
import fallClean from "@/assets/fall_clean.png";

const GUACCI = "https://farmaciaguacci.it/brahma-golden-pb-50ml";
const WA = (msg) => `https://wa.me/393758274001?text=${encodeURIComponent(msg)}`;

const PRODUCTS = [
  {
    id: "golden-pb",
    name: "Face Cream",
    short: "Golden PB",
    img: goldenClean,
    imgW: "w-[24%]",
    alt: "Flacone Brahma Golden PB Face Cream anti-age postbiotic technology",
    fit: "contain",
    text: "Trattamento viso anti-age con tecnologia postbiotica, per una pelle visibilmente più giovane, rigenerata e luminosa.",
    chips: ["50 ml", "Tecnologia postbiotica", "Filtri UVA-UVB"],
    accent: "text-gold-deep",
    glow: "bg-[#E8D3A6]",
    glowCore: "rgba(243, 228, 188, 0.85)",
    wash: "bg-gold-pale",
    testid: "product-card-golden-pb",
    buy: "btn-buy-guacci-golden",
    wa: "btn-whatsapp-golden",
    formula: "#formula-face-cream",
    waMsg: "Buongiorno, vorrei informazioni su FACE CREAM — Brahma Golden PB.",
  },
  {
    id: "pink-lady",
    name: "Face Serum",
    short: "Pink Lady",
    img: pinkClean,
    imgW: "w-[27%]",
    alt: "Flacone Brahma Pink Lady Face Serum anti-age",
    fit: "contain",
    text: "Un siero intensivo anti-age per idratare, rimpolpare e donare una naturale freschezza quotidiana.",
    chips: ["30 ml", "Anti-age quotidiano", "Freschezza naturale"],
    accent: "text-blush-deep",
    glow: "bg-[#F5D3D6]",
    glowCore: "rgba(249, 218, 213, 0.85)",
    wash: "bg-blush",
    testid: "product-card-pink-lady",
    buy: "btn-buy-guacci-pink",
    wa: "btn-whatsapp-pink",
    formula: "#formula-face-serum",
    waMsg: "Buongiorno, vorrei informazioni su FACE SERUM — Brahma Pink Lady.",
  },
  {
    id: "fall-water",
    name: "Cleansing Oil",
    short: "Fall Water",
    img: fallClean,
    imgW: "w-[32%]",
    alt: "Flacone Brahma Fall Water Cleansing Oil 150 ml",
    fit: "contain",
    text: "Un olio detergente e struccante delicato che rimuove le impurità, lasciando la pelle fresca, idratata e purificata.",
    chips: ["150 ml", "Detersione delicata", "Struccante"],
    accent: "text-sage-deep",
    glow: "bg-[#C9DECF]",
    glowCore: "rgba(217, 232, 217, 0.85)",
    wash: "bg-sage",
    testid: "product-card-fall-water",
    buy: "btn-buy-guacci-green",
    wa: "btn-whatsapp-green",
    formula: "#formula-cleansing-oil",
    waMsg: "Buongiorno, vorrei informazioni su CLEANSING OIL — Brahma Fall Water.",
  },
];

export const Products = () => (
  <section id="prodotti" className="relative mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-36">
    <Reveal>
      <span className="text-[11px] font-medium uppercase tracking-[0.42em] text-gold-deep">
        La linea
      </span>
      <h2 className="mt-4 font-display text-4xl font-light leading-tight text-ink lg:text-6xl">
        Tre gesti, <span className="italic text-gold-deep">un rituale</span>
      </h2>
    </Reveal>

    <div className="mt-14 grid gap-14 md:grid-cols-2 lg:grid-cols-3 lg:gap-10">
      {PRODUCTS.map((p) => (
        <Reveal key={p.id} className="h-full">
          <div data-testid={p.testid} id={p.id} className="flex h-full flex-col">
            <div className="relative w-full">
              <div
                className={`absolute -inset-3 rounded-[2rem] ${p.glow} opacity-40 blur-xl`}
                aria-hidden="true"
              />
              <a
                href={p.formula}
                onClick={scrollToHash(p.formula)}
                data-testid={`product-photo-${p.id}`}
                aria-label={`Vai alla formulazione di ${p.name} ${p.short}`}
                className="product-photo-link"
              >
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="group relative aspect-[3/4] overflow-hidden rounded-3xl border border-stone-200/50 bg-white/70 shadow-[0_24px_50px_rgba(28,28,30,0.08)] backdrop-blur-md"
              >
                <div
                  className="absolute inset-0"
                  style={{
                    background: `radial-gradient(75% 60% at 50% 45%, ${p.glowCore} 0%, rgba(255,255,255,0) 72%)`,
                  }}
                />

                <div
                  className="absolute left-1/2 -translate-x-1/2"
                  style={{
                    bottom: "22%",
                    width: "34%",
                    height: "2.4%",
                    borderRadius: "50%",
                    background: "#1C1C1E",
                    filter: "blur(6px)",
                    opacity: 0.25,
                  }}
                />

                <img
                  src={p.img}
                  alt={p.alt}
                  className={`absolute left-1/2 -translate-x-1/2 ${p.imgW} transition-transform duration-700 ease-out group-hover:scale-[1.03]`}
                  style={{ bottom: "23.5%" }}
                />

                <div className="absolute -bottom-4 left-6 z-10">
                  <span className="glass rounded-full border border-stone-200/60 px-4 py-1.5 text-[10px] uppercase tracking-[0.28em] text-ink/70 shadow-[0_8px_18px_rgba(28,28,30,0.08)]">
                    Brahma {p.short}
                  </span>
                </div>
              </motion.div>
              </a>
            </div>

            <div className="mt-8 flex w-full flex-1 flex-col items-center text-center">
              <h3
                className={`font-display text-3xl font-light uppercase tracking-[0.14em] lg:text-4xl ${p.accent}`}
              >
                {p.name}
              </h3>
              <p className="mt-4 w-full max-w-sm text-base font-light leading-relaxed text-ink/80 lg:min-h-[5.5rem]">
                {p.text}
              </p>

              <div className="mt-2 flex min-h-[3.5rem] flex-wrap items-start justify-center gap-2">
                {p.chips.map((c) => (
                  <span
                    key={c}
                    className="rounded-full border border-stone-200/60 bg-[#FAF7F0] px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-smoke"
                  >
                    {c}
                  </span>
                ))}
              </div>

              <a
                href={p.formula}
                onClick={scrollToHash(p.formula)}
                data-testid={`btn-formula-${p.id}`}
                className="mt-4 inline-flex items-center gap-1.5 border-b border-gold/40 pb-0.5 text-[10px] uppercase tracking-[0.22em] text-gold-deep transition-colors duration-300 hover:text-ink"
              >
                Scopri Formulazione &amp; INCI
                <MoveDown className="h-3 w-3" />
              </a>

              <div className="mt-auto flex w-full gap-3 pt-7">
                <a
                  href={GUACCI}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={p.buy}
                  className="flex flex-[1.35] items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-gold-grad px-2 py-3 text-[10px] uppercase tracking-[0.12em] text-white shadow-[0_10px_24px_rgba(154,123,56,0.32)] transition-transform duration-300 hover:scale-[1.03]"
                >
                  Acquista in farmacia
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
                <a
                  href={WA(p.waMsg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={p.wa}
                  className="flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-stone-200/60 bg-white/70 px-2 py-3 text-[10px] uppercase tracking-[0.12em] text-gold-deep transition-colors duration-300 hover:bg-[#FAF7F0]"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);
